import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import useStore from '../store/useStore';
import Toolbar from '../components/Toolbar';
import Canvas from '../components/Canvas';
import Sidebar from '../components/Sidebar';
import { useT } from '../i18n';
import { fetchTemplateById, createTemplateApi, updateTemplateApi } from '../api/templatesApi';

const ADMIN_EMAIL = 'davronking001@gmail.com';

const ICON_OPTIONS = [
  { value: 'blog', label: 'Blog' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'cart', label: 'Cart' },
  { value: 'custom', label: 'Custom' },
];

export default function TemplateEditorPage() {
  const t = useT();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const theme = useStore((s) => s.theme);
  const accessToken = useStore((s) => s.accessToken);
  const userEmail = useStore((s) => s.userEmail);
  const loadUserMe = useStore((s) => s.loadUserMe);
  const restoreFromJSON = useStore((s) => s.restoreFromJSON);
  const generateJSON = useStore((s) => s.generateJSON);
  const resetEditor = useStore((s) => s.resetEditor);
  const setTemplateMode = useStore((s) => s.setTemplateMode);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const language = useStore((s) => s.language);
  const addEntityNode = useStore((s) => s.addEntityNode);
  const addEnumNode = useStore((s) => s.addEnumNode);
  const undo = useStore((s) => s.undo);

  const [menuCollapsed, setMenuCollapsed] = useState(() => {
    return localStorage.getItem('editorSidebar') === 'closed';
  });
  const toggleMenu = () => {
    const next = !menuCollapsed;
    setMenuCollapsed(next);
    localStorage.setItem('editorSidebar', next ? 'closed' : 'open');
  };

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#f0ad4e');
  const [icon, setIcon] = useState('custom');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const [error, setError] = useState('');
  const baseIdRef = useRef(null);
  const builtinRef = useRef(false);

  // Auth + admin gating.
  useEffect(() => {
    if (!accessToken) { navigate('/'); return; }
    if (!userEmail) { loadUserMe(); return; }
    if (userEmail !== ADMIN_EMAIL) { navigate('/home'); }
  }, [accessToken, userEmail, loadUserMe, navigate]);

  // Activate template mode (hides Generate button in Toolbar).
  useEffect(() => {
    setTemplateMode(true);
    return () => {
      setTemplateMode(false);
      resetEditor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing template (from backend) OR start fresh.
  useEffect(() => {
    if (loaded) return;
    if (templateId) {
      fetchTemplateById(templateId)
        .then((tpl) => {
          setName(tpl.name || '');
          setDescription(tpl.description || '');
          setColor(tpl.color || '#f0ad4e');
          setIcon(tpl.icon || 'custom');
          baseIdRef.current = tpl.id;
          builtinRef.current = !!tpl.builtin;
          restoreFromJSON(tpl.json);
          setLoaded(true);
        })
        .catch((e) => {
          setError(e.message || 'Failed to load template');
          setLoaded(true);
        });
    } else {
      resetEditor();
      baseIdRef.current = null;
      builtinRef.current = false;
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Ctrl+Z undo.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      alert(t('admin.template.nameRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const schema = generateJSON();
      const payload = {
        name: name.trim(),
        description: description.trim(),
        color,
        icon,
        accent: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
        features: ['JWT Auth', 'Swagger', 'PostgreSQL', 'CORS'],
        builtin: builtinRef.current,
        json: schema,
      };
      const saved = baseIdRef.current
        ? await updateTemplateApi(baseIdRef.current, payload)
        : await createTemplateApi(payload);
      baseIdRef.current = saved.id;
      builtinRef.current = !!saved.builtin;
      setSavedAt(new Date().toLocaleTimeString());
      setTimeout(() => setSavedAt(''), 2500);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [name, description, color, icon, generateJSON, t]);

  if (!loaded) {
    return (
      <div className={`app ${theme}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--text-secondary)' }}>{t('home.loading')}</span>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div className={`app template-editor-page ${theme}`}>
        <div className="template-editor-bar">
          <button
            className="template-editor-back"
            onClick={() => navigate('/admin')}
            title={t('admin.template.backToAdmin')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M11 7H3M3 7l3-3M3 7l3 3" />
            </svg>
          </button>

          <div className="template-editor-fields">
            <input
              className="template-editor-input"
              type="text"
              placeholder={t('admin.template.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
            />
            <input
              className="template-editor-input wide"
              type="text"
              placeholder={t('admin.template.descPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={180}
            />
            <input
              className="template-editor-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              title={t('admin.template.color')}
            />
            <select
              className="template-editor-select"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              title={t('admin.template.icon')}
            >
              {ICON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="template-editor-actions">
            {error && <span className="template-editor-error">{error}</span>}
            {savedAt && <span className="template-editor-saved">✓ {t('save.saved')} {savedAt}</span>}
            <button
              className="template-editor-save"
              onClick={handleSave}
              disabled={saving || !name.trim()}
            >
              {saving ? t('admin.template.saving') : t('admin.template.save')}
            </button>
          </div>
        </div>

        <div className="main-layout template-editor-main">
          <Toolbar collapsed={menuCollapsed} onToggle={toggleMenu} />
          <div className="main-area">
            <Canvas />
            <Sidebar />
          </div>
        </div>

        {language && (
          <div className={`floating-add-buttons ${selectedNodeId ? 'sidebar-open' : ''}`}>
            <button className="floating-add-btn" onClick={() => addEntityNode()}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M7 2v10M2 7h10" />
              </svg>
              {t('toolbar.table')}
            </button>
            <button className="floating-add-btn enum" onClick={() => addEnumNode()}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M7 2v10M2 7h10" />
              </svg>
              {t('toolbar.enum')}
            </button>
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
}
